import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShoppingBag,
  ShoppingCart,
  Minus,
  Plus,
  Check,
  FileText,
  CreditCard,
  RefreshCw
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [signatureName, setSignatureName] = useState("");

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await fetch(`${API}/public/products/${productId}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
        
        // Fetch agreement if subscription product
        if (data.is_subscription) {
          fetchAgreement(productId);
        }
      } else {
        toast.error("Product not found");
        navigate("/store");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreement = async (prodId) => {
    try {
      const response = await fetch(`${API}/public/products`);
      // Note: We'd need a public endpoint for agreements, for now we'll handle this differently
    } catch (error) {
      console.error("Error fetching agreement:", error);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error("Please sign in to purchase");
      navigate("/login", { state: { from: `/product/${productId}` } });
      return;
    }

    // For subscription products, check if agreement needs to be signed
    if (product.is_subscription && !agreementSigned) {
      setShowAgreementModal(true);
      return;
    }

    setCheckoutLoading(true);

    try {
      const response = await fetch(`${API}/checkout/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({
          product_id: productId,
          quantity: quantity,
          origin_url: window.location.origin,
          is_subscription: product.is_subscription
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Checkout failed");
      }
    } catch (error) {
      toast.error("Failed to initiate checkout");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSignAgreement = () => {
    if (!signatureName.trim()) {
      toast.error("Please enter your name to sign");
      return;
    }
    setAgreementSigned(true);
    setShowAgreementModal(false);
    toast.success("Agreement signed successfully");
    // Now proceed to checkout
    handleCheckout();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/store" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Store</span>
            </Link>
            <Link to="/" className="flex items-center gap-3">
              <img src={COMPANY_LOGO} alt="V" className="h-8 w-auto" />
              <div className="h-6 w-px bg-slate-300" />
              <span className="font-brand text-sm font-bold text-slate-900">VEYRONIX</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {(product.image_url || product.images?.[0]) ? (
                <img
                  src={product.image_url || product.images?.[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ShoppingBag className="h-24 w-24 text-slate-300" />
                </div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {product.images.slice(1, 5).map((img, idx) => (
                  <div key={idx} className="aspect-square bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              {product.is_subscription && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full mb-3">
                  <RefreshCw className="h-3 w-3" />
                  Subscription
                </span>
              )}
              <h1 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 mb-2" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-slate-600 text-lg" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Price */}
            <div className="p-6 bg-white rounded-2xl border border-slate-200">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-heading text-4xl font-bold text-indigo-600" data-testid="product-price">
                  £{product.price.toFixed(2)}
                </span>
                {product.is_subscription && (
                  <span className="text-slate-500">
                    / {product.subscription_interval}
                  </span>
                )}
              </div>

              {/* Quantity */}
              {!product.is_subscription && (
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-slate-600">Quantity:</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"
                      data-testid="decrease-qty-btn"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <Input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 text-center h-10"
                      min="1"
                      data-testid="quantity-input"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"
                      data-testid="increase-qty-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Total */}
              {!product.is_subscription && (
                <div className="flex items-center justify-between py-4 border-t border-slate-200 mb-6">
                  <span className="text-slate-600">Total:</span>
                  <span className="font-heading text-2xl font-bold text-slate-900">
                    £{(product.price * quantity).toFixed(2)}
                  </span>
                </div>
              )}

              {/* Features for subscription */}
              {product.is_subscription && (
                <div className="space-y-3 mb-6 py-4 border-t border-slate-200">
                  {product.direct_debit_enabled && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Direct Debit Available
                    </div>
                  )}
                  {product.penalty_percentage && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <FileText className="h-4 w-4 text-amber-500" />
                      {product.penalty_percentage}% penalty for late payments
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <FileText className="h-4 w-4 text-indigo-500" />
                    Digital agreement required
                  </div>
                </div>
              )}

              <Button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                size="lg"
                className="w-full h-14 bg-indigo-600 hover:bg-indigo-700 rounded-full text-lg font-medium"
                data-testid="checkout-btn"
              >
                {checkoutLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </div>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    {product.is_subscription ? "Subscribe Now" : "Buy Now"}
                  </>
                )}
              </Button>

              {!user && (
                <p className="text-center text-sm text-slate-500 mt-4">
                  <Link to="/login" className="text-indigo-600 hover:underline">Sign in</Link> to complete your purchase
                </p>
              )}
            </div>

            {/* Stock info */}
            {!product.is_subscription && product.stock > 0 && (
              <p className="text-sm text-emerald-600 flex items-center gap-2">
                <Check className="h-4 w-4" />
                {product.stock} in stock
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Agreement Modal */}
      <Dialog open={showAgreementModal} onOpenChange={setShowAgreementModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Subscription Agreement</DialogTitle>
            <DialogDescription>
              Please review and sign the agreement to continue with your subscription.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg max-h-48 overflow-y-auto text-sm text-slate-600">
              <h4 className="font-semibold text-slate-900 mb-2">Terms & Conditions</h4>
              <p className="mb-2">
                By subscribing to {product.name}, you agree to:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Pay £{product.subscription_price || product.price} {product.subscription_interval}ly</li>
                {product.direct_debit_enabled && (
                  <li>Allow direct debit from your payment method</li>
                )}
                {product.penalty_percentage && (
                  <li>Accept a {product.penalty_percentage}% penalty charge for late or failed payments</li>
                )}
                <li>Cancel anytime with 30 days notice</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agree-terms"
                  checked={!!signatureName}
                  onCheckedChange={() => {}}
                  className="mt-1"
                />
                <label htmlFor="agree-terms" className="text-sm text-slate-600">
                  I have read and agree to the subscription terms and conditions
                </label>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Digital Signature (Type your full name)
                </label>
                <Input
                  type="text"
                  placeholder="Your Full Name"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  className="h-11"
                  data-testid="signature-input"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowAgreementModal(false)}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSignAgreement}
              disabled={!signatureName.trim()}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-full"
              data-testid="sign-agreement-btn"
            >
              Sign & Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetailPage;
